import type { GraphemeLessonPart } from "@-/dev.improve.haniverse.lib.level";
import { drillTypeCodec, type DrillType } from "@-/dev.improve.haniverse.lib.drill.entities";
import { useGetMorphemePanelsSlicesQueries } from "@-/dev.improve.haniverse.lib.morpheme.read";
import { createPageNavigator } from "@-/dev.improve.haniverse.lib.navigator.page";
import { useReviewCardMorphemePagePrefetch } from "@-/dev.improve.haniverse.routes.nav.app.learn.review.card.morpheme.navigator";
import { useCallback } from "react";
import { useRouter } from "@-/com.npmjs.expo-router";

export type LearnDeckNavigatorVariables =
  | { face?: "back" | "front"; kind: "slide"; slideSlug: string; view?: "info" | "study" }
  | { kind: "quiz"; quizSlug: string; source: "lesson" | "review" }
  | {
      contextSentence?: string | null;
      drillType: DrillType;
      face?: "back" | "front";
      grapheme: string;
      kind: "review";
      mode?: "manual" | "voice";
      view?: "info" | "study";
    }
  | {
      face?: "back" | "front";
      grapheme: string;
      kind: "lesson";
      part?: GraphemeLessonPart;
      /** Single-reading 2-P1 slot (干 gan4, then 干 gan1). */
      pinyin?: string;
      view?: "info" | "study";
    };

const LEARN_DECK_PATHNAME = "/(nav)/(app)/learn/deck" as const;

/**
 * Web only: pushes a history step as its own browser entry, so the browser's Back/Forward traverse
 * the steps. The step references an already mounted, prepared card, so there is no page to fetch.
 *
 * Native never writes a step to the router. A push added a native-stack screen that was never
 * removed (two per card, so the Learn stack grew for as long as the learner studied and the stall
 * after a rating climbed from ~1s to 2.3s within fifteen cards). Rewriting the route's params in
 * place instead changed the root navigation state on every reveal and rating, which made React
 * scan every retained tab. The deck host keeps native history in its own state.
 */
export function usePushLearnDeckHistoryEntry() {
  const router = useRouter();
  return useCallback(
    (params: Record<string, string>) => {
      // eslint-disable-next-line han-no-router-navigation/no-router-navigation -- Push an already prepared history checkpoint; no new page or queries are mounted.
      router.push({ params, pathname: LEARN_DECK_PATHNAME });
    },
    [router],
  );
}

/**
 * The deck route's href + prefetch queries for a card. Exported so the review and lesson entry
 * navigators (`useDrillPageNavigator`, `useNewMorphemeLessonPageNavigator`) share ONE definition of
 * what opening the deck on a card means.
 */
export function useLearnDeckNavigatorOptions() {
  const getReviewCardMorphemePagePrefetch = useReviewCardMorphemePagePrefetch();
  const getMorphemePanelsSlicesQueries = useGetMorphemePanelsSlicesQueries();

  return useCallback(
    (variables: LearnDeckNavigatorVariables) => {
      if (variables.kind === "slide") {
        return {
          href: {
            params: { face: "front", kind: "slide", slideSlug: variables.slideSlug, view: "study" },
            pathname: LEARN_DECK_PATHNAME,
          },
          queries: [],
        };
      }
      // A quiz, like a slide, renders from the bundled catalogue: nothing to fetch first.
      if (variables.kind === "quiz") {
        return {
          href: {
            params: {
              face: "front",
              kind: "quiz",
              quizSlug: variables.quizSlug,
              quizSource: variables.source,
              view: "study",
            },
            pathname: LEARN_DECK_PATHNAME,
          },
          queries: [],
        };
      }
      const params: Record<string, string> = {
        face: variables.face ?? "front",
        kind: variables.kind,
        morpheme: variables.grapheme,
        view: variables.view ?? "study",
      };
      if (variables.kind === "review") {
        params.type = drillTypeCodec.encode(variables.drillType) ?? "meaning";
        params.mode = variables.mode ?? "manual";
        if (variables.contextSentence != null) {
          params.contextSentence = variables.contextSentence;
        }
        return {
          href: { params, pathname: LEARN_DECK_PATHNAME },
          // Everything the review card's front, back and rating buttons read at mount: the
          // mnemonics slices, the drill slice, the FSRS resolution, the note, the word queries.
          queries: getReviewCardMorphemePagePrefetch({
            drillType: variables.drillType,
            morpheme: variables.grapheme,
            panel: "mnemonics",
          }),
        };
      }
      if (variables.part !== undefined) {
        params.part = variables.part;
      }
      const {
        mnemonicsPanelMorphemeSliceQueryOptions,
        mnemonicsPanelMorphemeUserSliceQueryOptions,
      } = getMorphemePanelsSlicesQueries(variables.grapheme);
      return {
        href: { params, pathname: LEARN_DECK_PATHNAME },
        queries: [
          mnemonicsPanelMorphemeSliceQueryOptions,
          mnemonicsPanelMorphemeUserSliceQueryOptions,
        ],
      };
    },
    [getMorphemePanelsSlicesQueries, getReviewCardMorphemePagePrefetch],
  );
}

export const useLearnDeckPageNavigator = createPageNavigator({
  useNavigator: useLearnDeckNavigatorOptions,
});

/**
 * The deck route's params for "the next card", whichever it is: the head of the study queue. The
 * deck host resolves which card that is and opens it, so a caller never waits for that answer.
 */
export const LEARN_DECK_QUEUE_HEAD_PARAMS = { kind: "next" } as const;

/**
 * Opens the deck on the head of the study queue (the Learn summary's Start). The route commits on
 * the call: the deck host owns resolving the card and shows its own loading, error and retry.
 * Nothing is prepared first, so opening can neither wait nor time out.
 */
export const useLearnDeckQueueHeadNavigator = createPageNavigator<void>({
  useNavigator: useLearnDeckQueueHeadNavigatorOptions,
});

function useLearnDeckQueueHeadNavigatorOptions() {
  return useCallback(
    () => ({
      href: { params: { ...LEARN_DECK_QUEUE_HEAD_PARAMS }, pathname: LEARN_DECK_PATHNAME },
      queries: [],
    }),
    [],
  );
}
